import { Component } from '@theme/component';

class TextShortenerComponent extends Component {
  requiredRefs = ['content', 'toggleButton'];

  #resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(() => this.#measure()) : null;

  connectedCallback() {
    super.connectedCallback();
    this.#observeLayout();
    this.refs.toggleButton.hidden = true;
    window.addEventListener('resize', this.#measure);
    requestAnimationFrame(() => this.#measure());
  }

  updatedCallback() {
    super.updatedCallback();
    this.dataset.expanded = 'false';
    this.refs.toggleButton?.setAttribute('aria-expanded', 'false');
    this.refs.toggleButton.hidden = true;
    this.#observeLayout();
    requestAnimationFrame(() => this.#measure());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#measure);
    this.#resizeObserver?.disconnect();
  }

  expand = (event) => {
    event.preventDefault();

    if (this.dataset.overflow !== 'true') return;

    this.dataset.expanded = 'true';
    this.refs.toggleButton.setAttribute('aria-expanded', 'true');
    this.refs.toggleButton.hidden = true;
  };

  #observeLayout() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver?.observe(this);

    if (this.refs.content instanceof HTMLElement) {
      this.#resizeObserver?.observe(this.refs.content);
    }

    const textBlock = this.#getTextBlock();

    if (textBlock instanceof HTMLElement) {
      this.#resizeObserver?.observe(textBlock);
    }
  }

  #getTextBlock() {
    if (!(this.refs.content instanceof HTMLElement)) return null;

    return this.refs.content.querySelector('.text-block');
  }

  #getMeasurementTarget() {
    const textBlock = this.#getTextBlock();

    if (!(textBlock instanceof HTMLElement)) return null;

    const visibleContentTarget = Array.from(textBlock.children).find(
      (child) =>
        child instanceof HTMLElement &&
        child.classList.contains('text-block__content') &&
        !child.hidden &&
        getComputedStyle(child).display !== 'none'
    );

    return visibleContentTarget instanceof HTMLElement ? visibleContentTarget : textBlock;
  }

  #getLineRects() {
    const measurementTarget = this.#getMeasurementTarget();

    if (!(measurementTarget instanceof HTMLElement)) return [];

    const range = document.createRange();
    range.selectNodeContents(measurementTarget);

    const lineRects = Array.from(range.getClientRects())
      .filter((rect) => rect.width > 0 && rect.height > 0)
      .reduce((lines, rect) => {
        const existingLine = lines.find((line) => Math.abs(line.top - rect.top) <= 1);

        if (existingLine) {
          existingLine.bottom = Math.max(existingLine.bottom, rect.bottom);
          return lines;
        }

        lines.push({
          top: rect.top,
          bottom: rect.bottom,
        });

        return lines;
      }, [])
      .sort((left, right) => left.top - right.top);

    if (typeof range.detach === 'function') {
      range.detach();
    }

    return lineRects;
  }

  #getCollapsedState() {
    const lineRects = this.#getLineRects();
    const lines = Number.parseInt(this.dataset.lines ?? '0', 10);

    if (!lineRects.length || !Number.isFinite(lines) || lines <= 0) {
      return {
        collapsedHeight: 0,
        hasOverflow: false,
      };
    }

    const visibleLineIndex = Math.min(lines, lineRects.length) - 1;

    return {
      collapsedHeight: Math.ceil(lineRects[visibleLineIndex].bottom - lineRects[0].top),
      hasOverflow: lineRects.length > lines,
    };
  }

  #measure = () => {
    if (!(this.refs.content instanceof HTMLElement)) return;

    const { collapsedHeight, hasOverflow } = this.#getCollapsedState();

    if (!collapsedHeight) {
      this.dataset.overflow = 'false';
      this.dataset.expanded = 'false';
      this.style.removeProperty('--text-block-collapsed-height');
      this.refs.toggleButton.setAttribute('aria-expanded', 'false');
      this.refs.toggleButton.hidden = true;
      return;
    }

    this.style.setProperty('--text-block-collapsed-height', `${collapsedHeight}px`);

    this.dataset.overflow = hasOverflow ? 'true' : 'false';
    this.refs.toggleButton.hidden = !hasOverflow || this.dataset.expanded === 'true';

    if (!hasOverflow) {
      this.dataset.expanded = 'false';
      this.refs.toggleButton.setAttribute('aria-expanded', 'false');
    }
  };
}

if (!customElements.get('text-shortener-component')) {
  customElements.define('text-shortener-component', TextShortenerComponent);
}
